import { EventBus } from "@engine/core/EventBus";

export type ItemId = number;
export type Rarity = "common" | "uncommon" | "rare" | "epic" | "legendary";
export type EquipSlot = "head" | "chest" | "legs" | "feet" | "hands" | "main_hand" | "off_hand" | "neck" | "ring";

export interface ItemDef {
  id: ItemId;
  name: string;
  iconKey?: string; // atlas frame name
  rarity: Rarity;
  stackable: boolean;
  maxStack: number;
  /** When equippable, the slot it goes in. */
  equipSlot?: EquipSlot;
  /** Free-form stat bag, additively applied when equipped. */
  stats?: Record<string, number>;
  /** When usable (consumable), called server-side; client may run hook for VFX. */
  onUse?: () => void;
}

export interface InventorySlot {
  itemId: ItemId;
  count: number;
}

export type InventoryEvents = {
  added: { itemId: ItemId; count: number; slot: number };
  removed: { itemId: ItemId; count: number; slot: number };
  moved: { from: number; to: number };
  full: { itemId: ItemId; count: number };
  equipped: { itemId: ItemId; slot: EquipSlot };
  unequipped: { itemId: ItemId; slot: EquipSlot };
};

export class ItemDB {
  private map = new Map<ItemId, ItemDef>();
  add(def: ItemDef): this {
    this.map.set(def.id, def);
    return this;
  }
  get(id: ItemId): ItemDef | undefined {
    return this.map.get(id);
  }
  has(id: ItemId): boolean {
    return this.map.has(id);
  }
}

/**
 * Inventory + equipment for one entity. Compose into Entity via:
 *
 *   declare module "@engine/ecs/components" {
 *     interface Entity { inventory?: Inventory; equipment?: Equipment; }
 *   }
 *
 * The class fires events through `bus` so the HUD and gameplay logic react
 * without polling.
 */
export class Inventory {
  readonly slots: Array<InventorySlot | null>;
  readonly bus = new EventBus<InventoryEvents>();

  constructor(
    private readonly db: ItemDB,
    public readonly capacity: number,
  ) {
    this.slots = new Array<InventorySlot | null>(capacity).fill(null);
  }

  /** Insert N copies of an item. Stacks where possible. Returns leftover. */
  add(itemId: ItemId, count: number): number {
    const def = this.db.get(itemId);
    if (!def) return count;
    let remaining = count;
    if (def.stackable) {
      for (let i = 0; i < this.slots.length && remaining > 0; i++) {
        const s = this.slots[i];
        if (!s || s.itemId !== itemId) continue;
        const can = Math.min(remaining, def.maxStack - s.count);
        if (can <= 0) continue;
        s.count += can;
        remaining -= can;
        this.bus.emit("added", { itemId, count: can, slot: i });
      }
    }
    while (remaining > 0) {
      const idx = this.slots.indexOf(null);
      if (idx < 0) break;
      const can = def.stackable ? Math.min(remaining, def.maxStack) : 1;
      this.slots[idx] = { itemId, count: can };
      remaining -= can;
      this.bus.emit("added", { itemId, count: can, slot: idx });
    }
    if (remaining > 0) this.bus.emit("full", { itemId, count: remaining });
    return remaining;
  }

  /** Remove N from a specific slot. */
  removeFromSlot(slotIdx: number, count: number): number {
    const s = this.slots[slotIdx];
    if (!s) return 0;
    const removed = Math.min(s.count, count);
    s.count -= removed;
    if (s.count <= 0) this.slots[slotIdx] = null;
    this.bus.emit("removed", { itemId: s.itemId, count: removed, slot: slotIdx });
    return removed;
  }

  move(from: number, to: number): boolean {
    if (from === to || from < 0 || to < 0 || from >= this.slots.length || to >= this.slots.length) return false;
    const a = this.slots[from] ?? null;
    const b = this.slots[to] ?? null;
    this.slots[from] = b;
    this.slots[to] = a;
    this.bus.emit("moved", { from, to });
    return true;
  }

  /** Try to merge `from` stack into `to` (must be same itemId and stackable). */
  merge(from: number, to: number): boolean {
    if (from === to) return false;
    const a = this.slots[from];
    const b = this.slots[to];
    if (!a || !b || a.itemId !== b.itemId) return false;
    const def = this.db.get(a.itemId);
    if (!def?.stackable) return false;
    const can = Math.min(a.count, def.maxStack - b.count);
    if (can <= 0) return false;
    b.count += can;
    a.count -= can;
    if (a.count <= 0) this.slots[from] = null;
    return true;
  }

  /** Count of a specific item across all slots. */
  countOf(itemId: ItemId): number {
    let n = 0;
    for (const s of this.slots) if (s?.itemId === itemId) n += s.count;
    return n;
  }

  /** First non-empty slot index for an item, or -1. */
  findSlot(itemId: ItemId): number {
    for (let i = 0; i < this.slots.length; i++) if (this.slots[i]?.itemId === itemId) return i;
    return -1;
  }
}

export class Equipment {
  private slots = new Map<EquipSlot, ItemId>();
  readonly bus = new EventBus<{
    equipped: InventoryEvents["equipped"];
    unequipped: InventoryEvents["unequipped"];
  }>();

  constructor(private readonly db: ItemDB) {}

  equip(itemId: ItemId): EquipSlot | undefined {
    const def = this.db.get(itemId);
    if (!def?.equipSlot) return undefined;
    const previous = this.slots.get(def.equipSlot);
    if (previous !== undefined) {
      this.slots.delete(def.equipSlot);
      this.bus.emit("unequipped", { itemId: previous, slot: def.equipSlot });
    }
    this.slots.set(def.equipSlot, itemId);
    this.bus.emit("equipped", { itemId, slot: def.equipSlot });
    return def.equipSlot;
  }

  unequip(slot: EquipSlot): ItemId | undefined {
    const id = this.slots.get(slot);
    if (id === undefined) return undefined;
    this.slots.delete(slot);
    this.bus.emit("unequipped", { itemId: id, slot });
    return id;
  }

  get(slot: EquipSlot): ItemId | undefined {
    return this.slots.get(slot);
  }

  /** Sum of stat bags across all equipped items. */
  totalStats(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const id of this.slots.values()) {
      const def = this.db.get(id);
      if (!def?.stats) continue;
      for (const [k, v] of Object.entries(def.stats)) out[k] = (out[k] ?? 0) + v;
    }
    return out;
  }
}

declare module "@engine/ecs/components" {
  interface Entity {
    inventory?: Inventory;
    equipment?: Equipment;
  }
}
